'use client';

import { ChangeEvent, FC, FormEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Conditional from '@next-app/components/Conditional';
import LLAlert from '@next-app/components/elements/LLAlert/LLAlert';
import LLButton from '@next-app/components/elements/LLButton/LLButton';
import LLInput from '@next-app/components/elements/LLInput/LLInput';
import {
  CHECKOUT_VALIDATION_MESSAGES,
  FIELD_VALIDATION_RULES,
} from '@next-app/constants/CheckoutConstants';
import {
  selectDynSess,
  selectIsFurniture,
  selectIsGuestUser,
  selectIsLoggedIn,
  selectLlmAPIVersion,
} from '@next-app/lib/features/InitSelectors';
import {
  setIsSubmitOrder,
  setLoadCheckoutDetails,
} from '@next-app/lib/features/checkout/CheckoutSlice';
import { isNotNullOrUndefinedorEmpty } from '@next-app/utils/common-utils';
import {
  applyGiftCardReq,
  handleAPIDownErrors,
  handleCheckoutServerErrors,
  sendOnBlurDTMEvent,
  triggerCheckoutFormErrorEvent,
  validateFields,
} from '@next-app/utils/CheckoutHelper';
import { GiftCardFormFields } from './GiftCardFormFields';
import parse from 'html-react-parser';
import {
  selectIsPureBopisOrder,
  selectPaymentDetailInfo,
  selectPaymentsInfo,
} from '@next-app/lib/features/checkout/CheckoutSelector';
import { DynamicDTMEvent } from '@next-app/utils/dtm-event';
import Loader from '@next-app/components/shared/Loader/Loader';

interface IGiftCardFormState {
  [GiftCardFormFields.GC_NUM]: string;
  [GiftCardFormFields.GC_AUTH]: string;
}

interface GiftCardFormProps {
  setShowCollapsed: Function;
  collapseOnSubmit?: boolean;
}

const GiftCardForm: FC<GiftCardFormProps> = ({
  setShowCollapsed,
  collapseOnSubmit = true,
}) => {
  const dispatch = useDispatch();
  const dynSessConf = useSelector(selectDynSess);
  const isLoggedInUser = useSelector(selectIsLoggedIn);
  const isUserGuest = useSelector(selectIsGuestUser);
  const isPureBopis = useSelector(selectIsPureBopisOrder);
  const paymentDetailsInfo = useSelector(selectPaymentDetailInfo);
  const paymentInfo = useSelector(selectPaymentsInfo);
  const isFurniture = useSelector(selectIsFurniture);
  const [loading, setLoading] = useState<boolean>(false);
  const llmAPIVersion = useSelector(selectLlmAPIVersion);

  // local state
  const [giftCardFormState, setGiftCardFormState] =
    useState<IGiftCardFormState>({
      [GiftCardFormFields.GC_NUM]: '',
      [GiftCardFormFields.GC_AUTH]: '',
    });

  const [giftCardFormErrors, setGiftCardFormErrors] =
    useState<Record<string, string>>();

  // methods
  /**
   * updateContactFormState
   * @param newState
   */
  const updateGiftCardFormState = (newState: Partial<IGiftCardFormState>) => {
    setGiftCardFormState({
      ...giftCardFormState,
      ...newState,
    });
  };

  /**
   * handleFormChange
   * @param event
   * @param fieldType
   */
  const handleFormChange = (
    event: ChangeEvent,
    fieldType: GiftCardFormFields.GC_NUM | GiftCardFormFields.GC_AUTH,
  ) => {
    let value = (event.target as HTMLInputElement).value;
    value = value.replace(/\D/g, '');
    setGiftCardFormErrors((prev) => ({ ...prev, [fieldType]: '' }));
    updateGiftCardFormState({
      [fieldType]: value ? parseInt(value) : '',
    });
  };

  /**
   * resetGCForm
   */
  const resetGCForm = () => {
    setGiftCardFormErrors({});
    updateGiftCardFormState({
      [GiftCardFormFields.GC_NUM]: '',
      [GiftCardFormFields.GC_AUTH]: '',
    });
    setShowCollapsed(true);
  };

  const validateGiftCardField = (name: string, value: string): string => {
    let errorMessage = '';
    switch (name) {
      case GiftCardFormFields.GC_NUM: {
        if (!value)
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.GIFT_CARD_NUMBER_REQUIRED;
        else if (!FIELD_VALIDATION_RULES.GIFT_CARD_NUM.test(value))
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.GIFT_CARD_NUMBER_INVALID;
        break;
      }
      case GiftCardFormFields.GC_AUTH: {
        if (!value)
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.GIFT_CARD_AUTH_REQUIRED;
        else if (!FIELD_VALIDATION_RULES.GIFT_CARD_AUTH.test(value))
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.GIFT_CARD_AUTH_INVALID;
        break;
      }
    }
    return errorMessage;
  };

  /**
   * applyGiftCard
   */
  const applyGiftCard = async (e: FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    setGiftCardFormErrors({});
    const isFormValid = validateFields(
      giftCardFormState,
      validateGiftCardField,
      setGiftCardFormErrors,
    );
    if (isFormValid) {
      setGiftCardFormErrors({});
      setLoading(true);
      // handle API interaction here
      try {
        const applyGiftCardRequest = {
          gcNumber: giftCardFormState[GiftCardFormFields.GC_NUM],
          gcAuthNumber: giftCardFormState[GiftCardFormFields.GC_AUTH],
          addGiftCard: isLoggedInUser,
          _dynSessConf: dynSessConf,
          howMany: -1,
          start: 0,
        };
        const applyGiftCardResponse = await applyGiftCardReq(
          applyGiftCardRequest,
          isFurniture,
        );

        if (applyGiftCardResponse?.checkoutResponse) {
          const eventData = {
            event: 'paymentWalletGC',
          };
          DynamicDTMEvent(eventData);
          dispatch(
            setLoadCheckoutDetails(applyGiftCardResponse?.checkoutResponse),
          );
          if (
            !(
              isUserGuest &&
              isPureBopis &&
              (paymentDetailsInfo.additionalPaymentRequired ||
                !paymentInfo?.address?.firstName ||
                !paymentInfo?.address?.lastName ||
                !paymentInfo?.address?.phoneNumber)
            )
          ) {
            dispatch(setIsSubmitOrder(true));
          }
          if (collapseOnSubmit) setShowCollapsed(true);
        }

        if (applyGiftCardResponse.formError) {
          triggerCheckoutFormErrorEvent({
            step: 'payment',
            llmAPIVersion,
            isFurniture,
            errors: applyGiftCardResponse?.formExceptions,
          });
          handleCheckoutServerErrors(
            applyGiftCardResponse.formExceptions,
            setGiftCardFormErrors,
          );
        }
      } catch (error) {
        // Handle unexpected errors here if needed
        console.error('Error applying gift card:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const getFormError = () => {
    const formErrorKeys: string[] = Object.keys(giftCardFormErrors ?? {});
    const propertyPathValues: string[] = Object.values(GiftCardFormFields);

    const globalFormErrorKeys: string[] = formErrorKeys?.filter(
      (key) => !propertyPathValues.includes(key),
    );

    return giftCardFormErrors?.[globalFormErrorKeys?.[0]] || '';
  };

  const onBlurHandler = (
    key: string,
    e: React.FocusEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    const errorMessage = validateGiftCardField(key, value);
    if (errorMessage) {
      setGiftCardFormErrors((prev) => ({ ...prev, [key]: errorMessage }));
    }
    sendOnBlurDTMEvent({ key, errorMessage, value });
  };

  return (
    <>
      <form className="needs-validation" onSubmit={applyGiftCard}>
        <div className="row g-ll-3">
          <div className="col-12 col-md-6">
            <LLInput
              testid="gcNum"
              type="text"
              value={giftCardFormState[GiftCardFormFields.GC_NUM]}
              errorMsg={
                giftCardFormErrors?.[GiftCardFormFields.GC_NUM] ||
                giftCardFormErrors?.[GiftCardFormFields.FORM_ERROR] ||
                giftCardFormErrors?.[GiftCardFormFields.GENERIC_ERROR] ||
                getFormError()
              }
              placeholder="Gift Card #"
              id="gcNum"
              onChange={(e) => handleFormChange(e, GiftCardFormFields.GC_NUM)}
              maxLength={16}
              onBlur={(e) => onBlurHandler(GiftCardFormFields.GC_NUM, e)}
              automationId="giftcard_number"
            />
          </div>
          <div className="col-12 col-md-6">
            <LLInput
              testid="gcAuthNum"
              type="text"
              value={giftCardFormState[GiftCardFormFields.GC_AUTH]}
              errorMsg={giftCardFormErrors?.[GiftCardFormFields.GC_AUTH]}
              placeholder="Authorization #"
              id="gcAuthNum"
              onChange={(e) => handleFormChange(e, GiftCardFormFields.GC_AUTH)}
              maxLength={4}
              onBlur={(e) => onBlurHandler(GiftCardFormFields.GC_AUTH, e)}
              automationId="auth_number"
            />
          </div>
        </div>
        <div className="d-flex align-items-center mt-ll-3 justify-content-md-end gap-ll-10">
          <LLButton
            type="reset"
            onClick={resetGCForm}
            variation="tertiary"
            customClass="flex-grow-1 flex-md-grow-0"
          >
            Cancel
          </LLButton>
          <LLButton
            type="submit"
            variation="primary"
            customClass="flex-grow-1 flex-md-grow-0"
            automationId="co_egc_apply_link"
          >
            Apply Gift Card
          </LLButton>
        </div>
      </form>
      <Conditional if={loading}>
        <Loader aria-label="Apply Gift Card Loader" />
      </Conditional>
    </>
  );
};

export default GiftCardForm;
